import React from 'react'
import { Button } from '@/components/ui/button'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useToast } from '@/hooks/useToast'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { Plus, Shuffle } from 'lucide-react'
import PaymentMethodCard, { type PaymentMethodSummary, type PayResult } from '@/components/billing/PaymentMethodCard'
import PaymentMethodFormDialog from '@/components/billing/PaymentMethodFormDialog'
import OrganizeHotelsDialog, {
  type OrganizeAssignment,
  type OrganizeHotel,
  type OrganizePreview,
} from '@/components/billing/OrganizeHotelsDialog'
import { type InvoiceRowData } from '@/components/billing/InvoiceRow'

/**
 * ONE PAYMENT METHOD (billing-payers §5). A management company runs several legal entities under
 * one business and each settles its own hotels on its own card. Every string here is composed
 * server-side — the page does zero date math, zero money math and no state classification.
 *
 * The operator's word is "payment method". "Payer", "wallet" and "profile" are ours and never
 * reach this file.
 */
export interface PaymentMethodRow {
  id: number
  name: string
  isDefault: boolean
  billingEmail: string | null
  paymentMethod: PaymentMethodSummary | null
  propertyNames: string[]
  alertState: string | null
  alertSentence: string | null
  /**
   * The invoice the alert sentence names, as a backend-served path. Absent when the payment
   * method owes nothing — and on a tab older than the serializer, where the row simply does not
   * become a link.
   */
  invoiceHref?: string | null
  /** This row's own PATCH endpoint, served whole by the backend. The page splices no ids. */
  updateHref?: string
  /**
   * PAY WHAT'S DUE (billing two clocks, 2026-10-05): this row's own payment endpoint and the
   * button's own words ("Pay £320.00 now", the total it owes), both served by the backend and both
   * absent unless the payment method owes money AND has a card the charge can use.
   */
  payHref?: string | null
  payLabel?: string | null
}

export interface PaymentMethodHrefs {
  create: string
  assign: string
  /**
   * THE SYNCHRONOUS VAULT (§11.1) — where the card dialog posts the SetupIntent the browser just
   * confirmed. Optional so a tab loaded before this shipped degrades to the resilient state (the
   * card is confirmed, it will appear shortly) rather than to the silence that started all this.
   */
  confirmCard?: string
}

/**
 * A REFUSAL WE ARE MEANT TO READ ALOUD: the server answered, and its message is a finished
 * sentence written for the operator. Tagged so it can never be confused with a thrown runtime
 * error — `fetch` rejects with a TypeError whose message is "Failed to fetch", and showing that to
 * a hotelier on the money page is exactly the slop this feature is not allowed to ship.
 */
class SaveRefused extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SaveRefused'
  }
}

// The one sentence this file is allowed to compose, and it is about the PAGE, not about billing:
// the request never reached a verdict, so there is nothing of the server's to quote.
const COULD_NOT_SAVE = 'We could not save that. Please try again.'

// A 200 that does not carry what the contract promises means this tab is older than the server.
const PAGE_IS_STALE = 'We could not save that. Refresh the page and try again.'

// The same two sentences for a payment, which is not a save. Both are about the REQUEST: whatever
// the server decided about the money arrives in its own sentence and is shown instead.
const COULD_NOT_PAY = 'We could not start that payment. Please try again.'
const PAYMENT_PAGE_IS_STALE = 'We could not start that payment. Refresh the page and try again.'

/**
 * The response bodies of the three self-serve endpoints, as one shape (the contract above
 * `Operator::BillingController`'s actions). Every field is optional here on purpose: a tab that
 * has been open across a deploy must fail as a readable sentence, never as a crash on a missing
 * key. `sentences` and `preview.sentences` are FINISHED SENTENCES — rendered, never parsed.
 */
interface BillingSaveResponse {
  payers?: PaymentMethodRow[]
  invoices?: InvoiceRowData[]
  movesDebt?: boolean
  sentences?: string[]
  preview?: { movesDebt?: boolean; blocked?: boolean; sentences?: string[] }
  /** The pay endpoint's answer: one finished sentence, and the tone the server chose for it. */
  sentence?: string
  tone?: 'success' | 'attention'
  error?: string
}

// The server's sentence when there is one, the session's when the token rotated, and ours only
// when neither of those exists. A raw runtime message never reaches the operator.
function refusalMessage(error: unknown): string {
  if (error instanceof SaveRefused) return error.message

  return apiErrorMessage(error, COULD_NOT_SAVE)
}

// SELF-SERVE PAYMENT-METHOD MANAGEMENT (docs/plans/2026-09-09-billing-payers.md §9).
//
// THE SECTION KEEPS ITS TWO SHAPES. A business that has never been split sees the one card surface
// it has always had, now with a quiet line under it explaining what a second one would be for; a
// business with several sees the list, the grouping controls, and the sentence that says what a
// regrouping does to money already owed. Plurality that is not there is noise — every operator on the
// fleet today is single-payment-method, and none of them should meet the word "Organize".
//
// REORGANISING IS ALWAYS ALLOWED (§10.2). The settle-first guard is gone: remapping while money
// was owed used to be a way out of a bill, and it is not one any more because the DEBT NOW FOLLOWS
// THE HOTELS. There is no state that closes this button, so there is no state to read — the
// dialog's preview step is where an operator learns what their grouping costs.
//
// AFTER A WRITE, THE STATE IS THE SERVER'S TOO: every success carries the re-read rows, and they
// replace the page's own. Nothing here is derived, guessed or optimistically patched.
export default function PaymentMethodsSection({
  payers,
  accountCard,
  hrefs,
  hotels,
  onPayersChange,
  onGroupingChanged,
  onManageCard,
  onPaid,
}: {
  payers: PaymentMethodRow[]
  /** The business's card, used only when the payers prop lags the frontend on an old page load. */
  accountCard: PaymentMethodSummary | null
  hrefs?: PaymentMethodHrefs
  hotels: OrganizeHotel[]
  onPayersChange: (payers: PaymentMethodRow[]) => void
  /**
   * A saved grouping (or a rename) also changes what the tables below print — "Paid by" on the
   * hotels, the payment method and the DOCUMENTS themselves on the invoices. The response carries
   * fresh invoices whenever it created any, so they are handed over here; whatever it did not
   * carry, the page refetches. One fact, one author, no chance of the blocks above and the table
   * below telling the operator two different things.
   */
  onGroupingChanged: (fresh?: { invoices?: InvoiceRowData[] }) => void
  onManageCard: (payer: PaymentMethodRow | null) => void
  /**
   * A payment changes more than this section: the invoice statuses (carried in the response, so
   * handed over), and the page-level readouts it does not carry (the strip, the banners, each
   * hotel's publishing answer), which the page refetches. With no argument the request did not
   * reach a verdict, so the page refetches everything a payment could have touched.
   */
  onPaid?: (fresh?: { invoices?: InvoiceRowData[] }) => void
}) {
  const { addToast } = useToast()
  const multiple = payers.length > 1

  const [formOpen, setFormOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<PaymentMethodRow | null>(null)
  const [formSaving, setFormSaving] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const [organizeOpen, setOrganizeOpen] = React.useState(false)
  const [organizeError, setOrganizeError] = React.useState<string | null>(null)

  // PAY WHAT'S DUE: which block has a payment in flight, and what the last payment on each block
  // said. Keyed by id, so re-rendering from fresh rows never loses an answer the operator is reading.
  const [payingId, setPayingId] = React.useState<number | null>(null)
  const [payResults, setPayResults] = React.useState<Record<number, PayResult>>({})

  function openAdd() {
    setEditing(null)
    setFormError(null)
    setFormOpen(true)
  }

  function openEdit(payer: PaymentMethodRow) {
    setEditing(payer)
    setFormError(null)
    setFormOpen(true)
  }

  function openOrganize() {
    setOrganizeError(null)
    setOrganizeOpen(true)
  }

  // One request shape for all three endpoints (the contract above BillingController's actions):
  // 200 carries the fresh state, 422 carries ONE finished sentence. The 422 sentence is returned
  // untouched — this component composes none.
  async function post(url: string, method: 'POST' | 'PATCH', body: unknown): Promise<BillingSaveResponse> {
    const response = await apiFetch(url, { method, body: JSON.stringify(body) })
    const data = (await response.json().catch(() => ({}))) as BillingSaveResponse

    if (response.ok) return data
    throw new SaveRefused(data.error ?? PAGE_IS_STALE)
  }

  // Every write answers with the re-read rows. A 200 without them is not a save this page can
  // render, so it is treated as the stale tab it is rather than left to blank the section.
  function payersFrom(data: BillingSaveResponse): PaymentMethodRow[] {
    if (!Array.isArray(data.payers)) throw new SaveRefused(PAGE_IS_STALE)

    return data.payers
  }

  async function submitForm(body: Record<string, string>) {
    const target = editing?.updateHref
    if (!editing && !hrefs?.create) return
    if (editing && !target) return

    setFormError(null)
    setFormSaving(true)
    try {
      const data = await post(editing ? target! : hrefs!.create, editing ? 'PATCH' : 'POST', body)
      onPayersChange(payersFrom(data))
      setFormOpen(false)
      if (editing) {
        addToast({ variant: 'success', title: 'Saved' })
        // A rename is printed by the tables below too.
        onGroupingChanged()
      } else {
        addToast({
          variant: 'success',
          title: 'Payment method added',
          description: `${body.name} has no hotels yet.`,
        })
      }
    } catch (e) {
      setFormError(refusalMessage(e))
    } finally {
      setFormSaving(false)
    }
  }

  // WHAT WOULD SAVING DO? The same endpoint, the same rows, `preview: true` — it writes nothing and
  // answers with the money sentences. Returning null means the request itself failed and the
  // dialog stays where it is with the server's (or the session's) sentence beside it.
  async function previewOrganize(assignments: OrganizeAssignment[]): Promise<OrganizePreview | null> {
    if (!hrefs?.assign) return null

    setOrganizeError(null)
    try {
      const { preview } = await post(hrefs.assign, 'POST', { preview: true, assignments })
      if (!preview) throw new SaveRefused(PAGE_IS_STALE)

      return {
        movesDebt: !!preview.movesDebt,
        blocked: !!preview.blocked,
        sentences: preview.sentences ?? [],
      }
    } catch (e) {
      setOrganizeError(refusalMessage(e))
      return null
    }
  }

  async function submitOrganize(assignments: OrganizeAssignment[]): Promise<boolean> {
    if (!hrefs?.assign) return false

    setOrganizeError(null)
    try {
      const data = await post(hrefs.assign, 'POST', { assignments })
      onPayersChange(payersFrom(data))
      setOrganizeOpen(false)
      addToast({
        variant: 'success',
        title: 'Hotels reorganised',
        // When money moved, the confirmation IS the server's own sentences — the same ones the
        // operator read a click ago, so the receipt and the promise are word-for-word the same.
        description:
          data.movesDebt && data.sentences?.length
            ? data.sentences.join(' ')
            : 'Invoices follow this grouping.',
      })
      // Fresh documents ride in the response whenever the save created any; the page takes them
      // from there and refetches only what the response could not carry.
      onGroupingChanged({ invoices: data.invoices })
      return true
    } catch (e) {
      setOrganizeError(refusalMessage(e))
      return false
    }
  }

  // ONE CLICK, ONE REQUEST, ONE ANSWER. The server walks the payment method's unpaid invoices oldest
  // first and answers with a finished sentence and the re-read rows, a declined card included (it is
  // a 200: the attempt was made and recorded). The sentence lands on the block it is about; the rows
  // replace the page's own. A 422 is a refusal in the server's words; a request that never reached
  // a verdict gets the page's one sentence. Nothing here adds, rounds or classifies money.
  async function payDue(payer: PaymentMethodRow) {
    const target = payer.payHref
    if (!target || payingId !== null) return

    setPayingId(payer.id)
    try {
      const response = await apiFetch(target, { method: 'POST', body: JSON.stringify({}) })
      const data = (await response.json().catch(() => ({}))) as BillingSaveResponse
      if (!response.ok) throw new SaveRefused(data.error ?? PAYMENT_PAGE_IS_STALE)
      if (!Array.isArray(data.payers) || !data.sentence) throw new SaveRefused(PAYMENT_PAGE_IS_STALE)

      onPayersChange(data.payers)
      setPayResults((current) => ({
        ...current,
        [payer.id]: { sentence: data.sentence!, tone: data.tone === 'success' ? 'success' : 'attention' },
      }))
      onPaid?.({ invoices: data.invoices })
    } catch (e) {
      const sentence = e instanceof SaveRefused ? e.message : apiErrorMessage(e, COULD_NOT_PAY)
      setPayResults((current) => ({ ...current, [payer.id]: { sentence, tone: 'error' } }))
      // An earlier invoice may have been paid before the fault struck: re-read rather than guess.
      onPaid?.()
    } finally {
      setPayingId(null)
    }
  }

  // The pay props for one block, so the two shapes below cannot wire the button differently.
  function payProps(payer: PaymentMethodRow | undefined) {
    if (!payer) return {}
    return {
      payLabel: payer.payHref ? (payer.payLabel ?? null) : null,
      onPay: payer.payHref ? () => payDue(payer) : null,
      paying: payingId === payer.id,
      payResult: payResults[payer.id] ?? null,
    }
  }

  const addButton = hrefs ? (
    <Button
      onClick={openAdd}
      variant={multiple ? 'outline' : 'ghost'}
      size="sm"
      className="h-8 shrink-0 rounded-lg"
    >
      <Plus className="mr-1.5 h-3.5 w-3.5" />
      Add payment method
    </Button>
  ) : null

  return (
    <>
      {multiple ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-foreground">Payment methods</h2>
                <HelpTooltip content="Each payment method pays for its own hotels and receives its own invoices. Add a card to each one. A payment that fails only affects the hotels that method pays for." />
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Each payment method has its own card and its own invoices.
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Invoices follow this grouping. Anything already due moves with its hotels when you save.
              </p>
            </div>

            <div className="flex items-center gap-2">
              {hrefs && (
                <Button onClick={openOrganize} variant="outline" size="sm" className="h-8 shrink-0 rounded-lg">
                  <Shuffle className="mr-1.5 h-3.5 w-3.5" />
                  Organize hotels
                </Button>
              )}
              {addButton}
            </div>
          </div>

          {payers.map((payer) => (
            <PaymentMethodCard
              key={payer.id}
              title={payer.name}
              billingEmail={payer.billingEmail}
              hotels={payer.propertyNames}
              showHotels
              alertState={payer.alertState}
              alertSentence={payer.alertSentence}
              alertInvoiceHref={payer.invoiceHref ?? null}
              paymentMethod={payer.paymentMethod}
              onManage={() => onManageCard(payer)}
              onEdit={payer.updateHref ? () => openEdit(payer) : null}
              {...payProps(payer)}
            />
          ))}
        </div>
      ) : (
        <div>
          <PaymentMethodCard
            paymentMethod={payers[0]?.paymentMethod ?? accountCard}
            billingEmail={payers[0]?.billingEmail ?? null}
            onManage={() => onManageCard(payers[0] ?? null)}
            onEdit={payers[0]?.updateHref ? () => openEdit(payers[0]) : null}
            {...payProps(payers[0])}
          />
          {addButton && (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                One card pays for every hotel here. Add another if a second company pays for some of them.
              </p>
              {addButton}
            </div>
          )}
        </div>
      )}

      <PaymentMethodFormDialog
        open={formOpen}
        mode={editing ? 'edit' : 'add'}
        initial={editing ? { name: editing.name, billingEmail: editing.billingEmail } : null}
        saving={formSaving}
        error={formError}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setFormError(null)
        }}
        onSubmit={submitForm}
      />

      <OrganizeHotelsDialog
        open={organizeOpen}
        hotels={hotels}
        methods={payers.map((payer) => ({ id: payer.id, name: payer.name }))}
        error={organizeError}
        onOpenChange={(open) => {
          setOrganizeOpen(open)
          if (!open) setOrganizeError(null)
        }}
        onPreview={previewOrganize}
        onSubmit={submitOrganize}
      />
    </>
  )
}
